/**
 * Issue #1671 (D465, D192.3): with the paywall on, a request refused with
 * 402 `{ code: "plan_required" }` opens Choose a plan, and sign-up ends there.
 *
 * Its twin is `lib/sessionExpiry.js` (a 401 means "no live session"). The same
 * argument makes a 402 safe to act on, and only this one: `requireActivePlan`
 * answers 402 `plan_required` for exactly one reason, a signed-in non-admin
 * whose subscription does not grant access. Admins never get it, and a paid
 * account never gets it, so there is nothing to be wrong about client-side.
 * Any other 402, or a 402 with another code, is not this module's business.
 *
 * NO LOOP. The redirect is refused on every page that is part of the way out
 * of the refusal: Choose a plan and its two return pages, Settings (where a
 * person adds a card), and the public pages (sign-in among them). Without
 * that, a gated read fired by Choose a plan itself would bounce it to itself.
 * A second, independent guard is the latch: one redirect per page load.
 *
 * The destination is a fixed literal plus one of two fixed reason words. No
 * part of the request, the response or the current URL is interpolated, so
 * this adds no open-redirect surface.
 */
import { PUBLIC_PATHS } from "./sessionExpiry";
import { ONBOARDING_PATH } from "./onboarding";
import { COACH_SETUP_PATH } from "./coachSetup";
import { COACH_TOUR_PATH, TOUR_PATH } from "./tour";

export const PLAN_PATH = "/plan";
export const PLAN_REQUIRED_CODE = "plan_required";
export const REASON_PARAM = "reason";
export const REASON_PLAN_REQUIRED = "plan_required";
export const REASON_SIGNUP = "signup";
export const PLAN_REASONS = Object.freeze([REASON_PLAN_REQUIRED, REASON_SIGNUP]);

export const planPathFor = (reason) => `${PLAN_PATH}?${REASON_PARAM}=${reason}`;
export const PLAN_REQUIRED_DESTINATION = planPathFor(REASON_PLAN_REQUIRED);
export const SIGNUP_PLAN_DESTINATION = planPathFor(REASON_SIGNUP);

/** The reason a /plan URL carries, or null. Only the two known words count. */
export function planReasonOf(search) {
  try {
    const v = new URLSearchParams(search || "").get(REASON_PARAM);
    return PLAN_REASONS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Pages on which a plan_required answer never moves the person: the exit's own pages, the public ones, and set-up
 * (D192.3, D476). Set-up is in the list because the app's own gates send an unpaid person TO it (`OnboardingGate`,
 * `CoachSetupGate`), and `/plan` is behind those same gates: without it the two bounce each other forever, one
 * document load per hop, and the per-load latch never holds. `planRequired.test.js` reads the gates' `Navigate`
 * targets and fails if one is not in this list.
 */
export const SETUP_PATHS = Object.freeze([ONBOARDING_PATH, COACH_SETUP_PATH, TOUR_PATH, COACH_TOUR_PATH]);
const NEVER_REDIRECT_FROM = Object.freeze(["/plan", "/settings", ...SETUP_PATHS, ...PUBLIC_PATHS.filter((p) => p !== "/")]);

function normalisePath(pathname) {
  return (pathname || "/").replace(/\/+$/, "").toLowerCase() || "/";
}

export function onGuardedPage(pathname) {
  const path = normalisePath(pathname);
  if (path === "/") return true;
  return NEVER_REDIRECT_FROM.some((p) => path === p || path.startsWith(`${p}/`));
}

/** "This answer is the paywall's refusal." Exactly 402 and exactly the code. */
export function isPlanRequired(error) {
  return error?.response?.status === 402 && error.response?.data?.code === PLAN_REQUIRED_CODE;
}

let redirectStarted = false;

/** Test-only. */
export function resetPlanRequiredLatch() {
  redirectStarted = false;
}

/** Leaves for Choose a plan, once. Returns whether it navigated. Replace, not push: Back does not return to the refused page. */
export function redirectToPlan() {
  if (redirectStarted) return false;
  if (typeof window === "undefined" || !window.location) return false;
  if (onGuardedPage(window.location.pathname)) return false;
  redirectStarted = true;
  window.location.replace(PLAN_REQUIRED_DESTINATION);
  return true;
}

/** Wires the above onto an axios instance. The error is re-rejected, never swallowed. */
export function installPlanRequiredInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isPlanRequired(error)) redirectToPlan();
      return Promise.reject(error);
    },
  );
  return instance;
}

/**
 * Where a finished sign-up goes. `target` is where it was going anyway. The server says whether the paywall is on
 * (`enforced` on `GET /api/billing/status`, an exempt read); when it is, and the account has no access, the answer
 * is Choose a plan. Anything else, including a failed read, keeps `target`: with the paywall off nothing changes,
 * and a read that fails must not strand anyone on a page they cannot leave.
 */
export async function destinationAfterSetUp(api, target) {
  try {
    const { data } = await api.get("/billing/status");
    if (data?.enforced === true && data?.grants_access !== true) return SIGNUP_PLAN_DESTINATION;
  } catch {
    // Not readable (billing unavailable, offline): fail open to where they were going.
  }
  return target;
}

/** True for a subscription that exists but no longer grants access because a payment failed: card needed, not a new plan. */
export function needsCardNotPlan(status) {
  return (status?.status === "unpaid" || status?.status === "past_due") && status?.grants_access === false;
}
